/**
 * OUTFIT — Algorithme de matching tenues
 *
 * Calcule des combinaisons cohérentes basées sur :
 * - Catégories complémentaires (règles mode)
 * - Harmonie des couleurs (roue chromatique + neutres)
 * - Saison / température du jour
 * - Style / occasion
 * - Tissu / matière (été vs hiver)
 * - Pièces manquantes → suggestions catalogue
 */

/* ══════════════════════════════════════════════════════════
   1. DONNÉES DE RÉFÉRENCE
══════════════════════════════════════════════════════════ */

// Extraction couleur depuis texte (nom + description)
var COLOR_KEYWORDS = {
  noir:     ['noir','black','dark','noire','noirci'],
  blanc:    ['blanc','white','blanche','ivoire','ivory','off-white','crème','cream'],
  gris:     ['gris','grey','gray','anthracite','charbon'],
  beige:    ['beige','nude','écru','ecru','sable','sand','wheat'],
  camel:    ['camel','taupe','cognac','noisette','fauve'],
  marine:   ['marine','navy','indigo','bleu nuit'],
  bleu:     ['bleu','blue','azur','cobalt','cyan','turquoise','denim'],
  rouge:    ['rouge','red','cerise','cherry','corail','coral','tomato','tomate'],
  bordeaux: ['bordeaux','burgundy','wine','vin','grenat','garnet','prune'],
  rose:     ['rose','pink','fuchsia','pêche','peach','blush','mauve','lilas'],
  vert:     ['vert','green','olive','sauge','sage','emerald','émeraude','forêt','forest'],
  kaki:     ['kaki','khaki','militaire','army','olive'],
  marron:   ['marron','brun','brown','chocolat','chocolate','nougat','moka','mocha'],
  or:       ['or','gold','doré','golden','jaune','yellow'],
  argent:   ['argent','silver','argenté','platine','platinum'],
  violet:   ['violet','purple','prune','lilas','lavande','lavender'],
  orange:   ['orange','rouille','rust','cuivre','copper','terracotta','terra'],
};

export function extractColorFromText(text) {
  if (!text) return null;
  var lower = text.toLowerCase();
  // Tester chaque couleur
  for (var color in COLOR_KEYWORDS) {
    var keywords = COLOR_KEYWORDS[color];
    for (var i = 0; i < keywords.length; i++) {
      if (lower.includes(keywords[i])) return color;
    }
  }
  return null;
}

// Extraire les infos d'un article (couleur, matière, style) depuis nom+desc
export function enrichItemFromText(item) {
  var text = [item.name, item.description, item.colorName, item.color].filter(Boolean).join(' ');
  var extractedColor = extractColorFromText(text) || item.color || null;

  // Extraire matière depuis le texte
  var materials = Object.keys(FABRIC_SEASON);
  var detectedMaterial = null;
  var lower = text.toLowerCase();
  for (var m = 0; m < materials.length; m++) {
    if (lower.includes(materials[m])) { detectedMaterial = materials[m]; break; }
  }

  // Extraire style depuis le texte
  var styleKeywords = {
    Soirée:  ['soirée','evening','gala','cocktail','satin','velours','dentelle'],
    Bureau:  ['bureau','office','tailleur','blazer','costume','professionnel'],
    Casual:  ['casual','everyday','quotidien','basique','basic','jean','denim'],
    Sport:   ['sport','gym','running','yoga','fitness','training','legging'],
    Chic:    ['chic','élégant','elegant','luxe','luxury','designer'],
  };
  var detectedStyle = item.style || null;
  if (!detectedStyle) {
    for (var style in styleKeywords) {
      var kws = styleKeywords[style];
      for (var k = 0; k < kws.length; k++) {
        if (lower.includes(kws[k])) { detectedStyle = style; break; }
      }
      if (detectedStyle) break;
    }
  }

  return Object.assign({}, item, {
    color:    extractedColor,
    material: detectedMaterial,
    style:    detectedStyle || item.style || 'Casual',
    _textAnalyzed: true,
  });
}

// Harmonie des couleurs — roue chromatique simplifiée
const COLOR_FAMILIES = {
  noir:       { neutral: true,  warm: false, cool: true,  family: 'dark'    },
  blanc:      { neutral: true,  warm: false, cool: false, family: 'light'   },
  gris:       { neutral: true,  warm: false, cool: true,  family: 'neutral' },
  beige:      { neutral: true,  warm: true,  cool: false, family: 'neutral' },
  camel:      { neutral: true,  warm: true,  cool: false, family: 'neutral' },
  crème:      { neutral: true,  warm: true,  cool: false, family: 'neutral' },
  ecru:       { neutral: true,  warm: true,  cool: false, family: 'neutral' },
  marine:     { neutral: false, warm: false, cool: true,  family: 'blue'    },
  bleu:       { neutral: false, warm: false, cool: true,  family: 'blue'    },
  navy:       { neutral: false, warm: false, cool: true,  family: 'blue'    },
  rouge:      { neutral: false, warm: true,  cool: false, family: 'red'     },
  bordeaux:   { neutral: false, warm: true,  cool: false, family: 'red'     },
  rose:       { neutral: false, warm: true,  cool: false, family: 'pink'    },
  vert:       { neutral: false, warm: false, cool: true,  family: 'green'   },
  kaki:       { neutral: true,  warm: true,  cool: false, family: 'green'   },
  marron:     { neutral: true,  warm: true,  cool: false, family: 'brown'   },
  or:         { neutral: false, warm: true,  cool: false, family: 'metal'   },
  argent:     { neutral: false, warm: false, cool: true,  family: 'metal'   },
  multicolore:{ neutral: true,  warm: true,  cool: true,  family: 'multi'   },
};

// Couleurs qui vont bien ensemble (paires harmonieuses)
const COLOR_HARMONY = {
  noir:    ['blanc','beige','camel','gris','rouge','rose','or','argent','marine','crème'],
  blanc:   ['noir','marine','bleu','rouge','beige','kaki','or','argent'],
  beige:   ['blanc','noir','marron','camel','bordeaux','kaki','or','marine'],
  camel:   ['blanc','noir','beige','bordeaux','marine','crème','or'],
  gris:    ['blanc','noir','rose','bleu','marine','bordeaux','argent'],
  marine:  ['blanc','beige','camel','gris','rouge','or','crème'],
  bleu:    ['blanc','gris','beige','camel','rouge','or'],
  bordeaux:['beige','camel','gris','crème','or','noir'],
  rouge:   ['noir','blanc','marine','gris'],
  rose:    ['noir','gris','blanc','marine','or'],
  kaki:    ['beige','blanc','marron','camel','or'],
  marron:  ['beige','camel','crème','or','kaki'],
};

// Matières par saison
const FABRIC_SEASON = {
  // Été / Chaud
  lin:        { seasons: ['été'],          minTemp: 20,  maxTemp: 99  },
  coton:      { seasons: ['été','mi-saison'], minTemp: 15, maxTemp: 99 },
  soie:       { seasons: ['été','soirée'],  minTemp: 18,  maxTemp: 99  },
  viscose:    { seasons: ['été','mi-saison'], minTemp: 15, maxTemp: 99 },
  dentelle:   { seasons: ['été','soirée'],  minTemp: 18,  maxTemp: 99  },
  // Mi-saison
  jersey:     { seasons: ['mi-saison','été'], minTemp: 12, maxTemp: 99 },
  denim:      { seasons: ['mi-saison','hiver'], minTemp: 5, maxTemp: 25 },
  // Hiver / Froid
  laine:      { seasons: ['hiver','mi-saison'], minTemp: -99, maxTemp: 18 },
  cachemire:  { seasons: ['hiver'],         minTemp: -99, maxTemp: 12  },
  velours:    { seasons: ['hiver','soirée'], minTemp: -99, maxTemp: 15  },
  cuir:       { seasons: ['hiver','mi-saison'], minTemp: -99, maxTemp: 18 },
  fourrure:   { seasons: ['hiver'],         minTemp: -99, maxTemp: 8   },
  polaire:    { seasons: ['hiver'],         minTemp: -99, maxTemp: 10  },
};

// Règles de combinaison par catégorie — quelles pièces vont ensemble
const OUTFIT_RULES = {
  // Une tenue complète = base + complement + shoes + optionnel
  base: ['Robes', 'Hauts', 'Bas'],
  complement: {
    Robes:  { requires: ['Chaussures'], optional: ['Vestes','Sacs','Bijoux','Accessoires'] },
    Hauts:  { requires: ['Bas','Chaussures'], optional: ['Vestes','Sacs','Bijoux','Accessoires'] },
    Bas:    { requires: ['Hauts','Chaussures'], optional: ['Vestes','Sacs','Bijoux','Accessoires'] },
  },
  // Poids de chaque catégorie dans le score
  weights: {
    Robes: 3, Hauts: 3, Bas: 3,
    Chaussures: 2, Vestes: 2,
    Sacs: 1, Bijoux: 1, Accessoires: 1,
  },
};

// Températures → saison
function getSeason(tempC) {
  if (tempC >= 25) return 'été';
  if (tempC >= 15) return 'mi-saison';
  return 'hiver';
}

// Température → catégories recommandées
function getRecommendedCategories(tempC) {
  if (tempC >= 25) return { required: ['Hauts','Bas'], avoid: ['Vestes'], robe: true };
  if (tempC >= 18) return { required: ['Hauts','Bas'], optional: ['Vestes'], robe: true };
  if (tempC >= 10) return { required: ['Hauts','Bas','Vestes'], robe: false };
  return { required: ['Hauts','Bas','Vestes'], avoid: ['Robes'], robe: false };
}

/* ══════════════════════════════════════════════════════════
   2. SCORING COULEUR
══════════════════════════════════════════════════════════ */
function colorCompatibilityScore(color1, color2) {
  if (!color1 || !color2) return 0.5; // inconnu → score moyen
  const c1 = color1.toLowerCase().trim();
  const c2 = color2.toLowerCase().trim();
  if (c1 === c2) return 0.7; // même couleur = acceptable mais pas idéal
  if (c1 === 'multicolore' || c2 === 'multicolore') return 0.6;

  const harmony1 = COLOR_HARMONY[c1] || [];
  if (harmony1.includes(c2)) return 1.0; // combo parfaite

  const fam1 = COLOR_FAMILIES[c1];
  const fam2 = COLOR_FAMILIES[c2];
  if (!fam1 || !fam2) return 0.5;

  if (fam1.neutral || fam2.neutral) return 0.8; // neutre avec n'importe quoi = bien
  if (fam1.warm === fam2.warm) return 0.65; // même température = acceptable
  return 0.3; // couleurs incompatibles
}

function outfitColorScore(items) {
  if (items.length < 2) return 1.0;
  var total = 0, count = 0;
  for (var i = 0; i < items.length; i++) {
    for (var j = i + 1; j < items.length; j++) {
      total += colorCompatibilityScore(items[i].color, items[j].color);
      count++;
    }
  }
  return count > 0 ? total / count : 1.0;
}

/* ══════════════════════════════════════════════════════════
   3. SCORING SAISON / MATIÈRE
══════════════════════════════════════════════════════════ */
function fabricSeasonScore(item, tempC) {
  var name = (item.name + ' ' + (item.material || '') + ' ' + (item.description || '')).toLowerCase();
  var season = getSeason(tempC);

  for (var fabric in FABRIC_SEASON) {
    if (name.includes(fabric)) {
      var info = FABRIC_SEASON[fabric];
      if (tempC >= info.minTemp && tempC <= info.maxTemp) return 1.0;
      if (info.seasons.includes(season)) return 0.9;
      return 0.2; // mauvaise saison
    }
  }

  // Pas de matière détectée → score neutre selon catégorie
  var cat = item.category || '';
  if (cat === 'Chaussures' || cat === 'Bijoux') return 0.9;
  if (cat === 'Vestes' && tempC < 18) return 1.0;
  if (cat === 'Vestes' && tempC > 22) return 0.4;
  return 0.75;
}

/* ══════════════════════════════════════════════════════════
   4. SCORE GLOBAL D'UNE COMBINAISON
══════════════════════════════════════════════════════════ */
export function scoreOutfit(items, tempC, occasion) {
  if (!items || items.length === 0) return 0;
  tempC = tempC || 18;

  // ═══ Si au moins un article a ai_metadata → utiliser le scoring IA ═══
  // Calibration plus réaliste (couleur + formality + style + occasion + pattern + saison + genre)
  // + bonus si l'outfit colle aux préférences déclarées par l'user au cold start.
  // Fallback sur l'algo legacy si rien d'analysé.
  try {
    const hasAI = items.some(i => i && i.ai_metadata && Object.keys(i.ai_metadata).length > 0);
    if (hasAI) {
      const { outfitMatchScore } = require('./AIMatchingService');
      // Daily prefs : lecture synchrone du cache (peut être null)
      let dailyPrefs = null;
      try {
        const { getCachedPrefs } = require('./DailyPreferencesService');
        dailyPrefs = getCachedPrefs();
      } catch {}
      // userAgeGroup + userSilhouette : context complet pour le scoring IA
      let userAgeGroup = null;
      let userSilhouette = null;
      try {
        const m = require('./UserContextService');
        if (m && typeof m.getCachedUserAgeGroup   === 'function') userAgeGroup   = m.getCachedUserAgeGroup();
        if (m && typeof m.getCachedUserSilhouette === 'function') userSilhouette = m.getCachedUserSilhouette();
      } catch {}
      const aiScore = outfitMatchScore(items, { tempC, occasion, dailyPrefs, userAgeGroup, userSilhouette });
      return aiScore;
    }
  } catch (e) {
    // En cas d'erreur d'import ou de calcul, on retombe sur le legacy ci-dessous
    console.warn('[scoreOutfit] AI matching failed, fallback legacy:', e.message);
  }

  // ═══ Algorithme legacy (couleur + saison + complétude + style) ═══
  // Score couleur (40%)
  var colorScore = outfitColorScore(items);

  // Score saison (30%)
  var seasonScores = items.map(function(i) { return fabricSeasonScore(i, tempC); });
  var seasonScore  = seasonScores.reduce(function(a, b) { return a + b; }, 0) / seasonScores.length;

  // Score complétude (20%) — combinaison complète = note meilleure
  var cats = items.map(function(i) { return i.category; });
  var hasBase  = cats.includes('Robes') || (cats.includes('Hauts') && cats.includes('Bas'));
  var hasShoes = cats.includes('Chaussures');
  var completeness = (hasBase ? 0.6 : 0) + (hasShoes ? 0.4 : 0);

  // Score style / occasion (10%)
  var styleScore = 1.0;
  if (occasion) {
    var mismatched = items.filter(function(i) {
      return i.style && i.style.toLowerCase() !== occasion.toLowerCase();
    }).length;
    styleScore = Math.max(0.4, 1 - mismatched * 0.2);
  }

  var total = colorScore * 0.40 + seasonScore * 0.30 + completeness * 0.20 + styleScore * 0.10;
  return Math.round(total * 100);
}

/* ══════════════════════════════════════════════════════════
   5. GÉNÉRER LES COMBINAISONS DEPUIS LE DRESSING
══════════════════════════════════════════════════════════ */
export function generateOutfits(wardrobeItems, tempC, occasion, maxOutfits) {
  tempC      = tempC      || 18;
  maxOutfits = maxOutfits || 12;

  // Si "Tout" → générer pour chaque occasion et mélanger
  var ALL_OCCASIONS = ['Casual', 'Soirée', 'Bureau', 'Chic', 'Sport'];
  if (!occasion || occasion === 'Tout') {
    var allResults = [];
    // ═══ FIX 2026-04-26 : adapter le quota par occasion à maxOutfits demandé ═══
    // Avant : 3 par occasion × 5 = 15 max. Pour un Premium qui demande 30+,
    // on plafonnait à 15. Maintenant : on répartit maxOutfits sur 5 occasions
    // avec un minimum de 3, mais permettant d'aller plus haut.
    var perOccasion = Math.max(3, Math.ceil(maxOutfits / ALL_OCCASIONS.length));
    ALL_OCCASIONS.forEach(function(occ) {
      var sub = generateOutfits(wardrobeItems, tempC, occ, perOccasion);
      allResults = allResults.concat(sub);
    });
    // Trier par score, dédupliquer (même combo d'articles)
    allResults.sort(function(a,b){ return b.score - a.score; });
    var seenIds = new Set();
    var unique = allResults.filter(function(o) {
      var key = o.items.map(function(i){return i.id;}).sort().join(',');
      if (seenIds.has(key)) return false;
      seenIds.add(key);
      return true;
    });
    return unique.slice(0, maxOutfits);
  }

  var recCats = getRecommendedCategories(tempC);

  // Enrichir chaque article
  var enrichedItems = wardrobeItems.map(function(item) {
    return item._textAnalyzed ? item : enrichItemFromText(item);
  });

  // Filtrer les articles par style/occasion si un filtre est actif
  var filtered = enrichedItems;
  if (occasion) {
    var occ = occasion.toLowerCase();
    // Garder les articles dont le style correspond, + articles neutres (Casual, sans style)
    var styleFiltered = enrichedItems.filter(function(item) {
      var st = (item.style || item.detectedStyle || 'Casual').toLowerCase();
      // Toujours garder les accessoires, sacs, bijoux, chaussures (polyvalents)
      var polyvalent = ['Chaussures','Sacs','Bijoux','Accessoires'].includes(item.category);
      if (polyvalent) return true;
      // Casual = accepté partout sauf Soirée stricte
      if (st === 'casual' && occ !== 'soirée') return true;
      // Correspondance directe
      if (st === occ) return true;
      // Chic accepté en Bureau et Soirée
      if (st === 'chic' && (occ === 'bureau' || occ === 'soirée')) return true;
      // Bureau accepté en Chic
      if (st === 'bureau' && occ === 'chic') return true;
      return false;
    });
    // Si le filtre réduit trop (moins de 2 articles de base), utiliser tous les articles
    var baseFiltered = styleFiltered.filter(function(i) {
      return ['Robes','Hauts','Bas'].includes(i.category);
    });
    if (baseFiltered.length >= 2) filtered = styleFiltered;
  }

  // Grouper par catégorie
  var byCat = {};
  filtered.forEach(function(item) {
    var cat = item.category || 'Autres';
    if (!byCat[cat]) byCat[cat] = [];
    byCat[cat].push(item);
  });

  var robeOutfits  = [];
  var hautBasOutfits = [];

  // ── Stratégie 1 : Robe + accessories ──
  if (recCats.robe !== false && byCat['Robes'] && byCat['Robes'].length > 0) {
    byCat['Robes'].forEach(function(robe) {
      // Générer plusieurs variantes par robe (avec/sans veste, sacs différents)
      var baseCombos = [[robe]];

      // Variante avec veste
      if (byCat['Vestes'] && byCat['Vestes'].length > 0) {
        var jacket = bestMatch(byCat['Vestes'], robe, tempC);
        if (jacket) baseCombos.push([robe, jacket]);
      }

      baseCombos.forEach(function(base) {
        // Chaque variante de chaussure = un look différent
        var shoes = byCat['Chaussures'] || [];
        var usedShoes = shoes.slice(0, 2); // max 2 paires par robe
        if (usedShoes.length === 0) usedShoes = [null];

        usedShoes.forEach(function(shoe) {
          var combo = base.slice();
          if (shoe) combo.push(shoe);

          // Sac assorti
          if (byCat['Sacs'] && byCat['Sacs'].length > 0) {
            var bag = bestMatch(byCat['Sacs'], robe, tempC);
            if (bag && !combo.find(function(c){return c.id===bag.id;})) combo.push(bag);
          }
          // Bijou
          if (byCat['Bijoux'] && byCat['Bijoux'].length > 0) {
            var jewel = byCat['Bijoux'][0];
            if (!combo.find(function(c){return c.id===jewel.id;})) combo.push(jewel);
          }

          if (combo.length >= 1) {
            robeOutfits.push(makeOutfit(combo, tempC, occasion, 'Robe'));
          }
        });
      });
    });
  }

  // ── Stratégie 2 : Haut + Bas + accessories ──
  if (byCat['Hauts'] && byCat['Bas']) {
    byCat['Hauts'].forEach(function(haut) {
      byCat['Bas'].forEach(function(bas) {
        // Vérifier compatibilité couleur minimale
        var score = colorCompatScore(haut, bas);
        if (score < 40) return;

        var combo = [haut, bas];

        // Veste selon température
        if (byCat['Vestes'] && byCat['Vestes'].length > 0) {
          var jacket = bestMatch(byCat['Vestes'], haut, tempC);
          if (jacket) combo.push(jacket);
        }

        // Chaussures : générer 2 looks avec 2 paires différentes
        var shoes = byCat['Chaussures'] || [];
        var usedShoes = shoes.slice(0, 2);
        if (usedShoes.length === 0) usedShoes = [null];

        usedShoes.forEach(function(shoe) {
          var c2 = combo.slice();
          if (shoe && !c2.find(function(x){return x.id===shoe.id;})) c2.push(shoe);

          // Sac
          if (byCat['Sacs'] && byCat['Sacs'].length > 0) {
            var bag = bestMatch(byCat['Sacs'], haut, tempC);
            if (bag && !c2.find(function(x){return x.id===bag.id;})) c2.push(bag);
          }
          // Bijou
          if (byCat['Bijoux'] && byCat['Bijoux'].length > 0) {
            var jewel = byCat['Bijoux'][0];
            if (!c2.find(function(x){return x.id===jewel.id;})) c2.push(jewel);
          }

          if (c2.length >= 2) {
            hautBasOutfits.push(makeOutfit(c2, tempC, occasion, 'Haut+Bas'));
          }
        });
      });
    });
  }

  // Trier chaque groupe par score
  robeOutfits.sort(function(a,b){ return b.score - a.score; });
  hautBasOutfits.sort(function(a,b){ return b.score - a.score; });

  // Dédupliquer chaque groupe séparément (éviter trop d'articles en commun)
  function dedupGroup(group, minUnique) {
    var unique = [];
    group.forEach(function(o) {
      var isDup = unique.some(function(u) {
        var shared = o.items.filter(function(i) {
          return u.items.some(function(j){ return j.id === i.id; });
        }).length;
        // Tolérer 1 article en commun max (pour les sacs/bijoux partagés)
        return shared >= Math.min(o.items.length, u.items.length);
      });
      if (!isDup) unique.push(o);
    });
    return unique;
  }

  var uniqueRobe   = dedupGroup(robeOutfits, 2);
  var uniqueHautBas = dedupGroup(hautBasOutfits, 2);

  // Garantir au moins 2 looks de chaque type si disponible
  var result = [];
  var robeSlice   = uniqueRobe.slice(0, Math.max(2, Math.floor(maxOutfits * 0.4)));
  var hbSlice     = uniqueHautBas.slice(0, Math.max(2, Math.floor(maxOutfits * 0.6)));
  result = result.concat(robeSlice).concat(hbSlice);

  // Compléter si pas assez
  if (result.length < maxOutfits) {
    var remaining = maxOutfits - result.length;
    var extra = uniqueRobe.slice(robeSlice.length).concat(uniqueHautBas.slice(hbSlice.length));
    result = result.concat(extra.slice(0, remaining));
  }

  return result.slice(0, maxOutfits);
}

function colorCompatScore(item1, item2) {
  var NEUTRALS = ['noir','blanc','beige','gris','marine','camel','ecru','ivoire','creme','nude'];
  var c1 = (item1.color || item1.extractedColor || '').toLowerCase();
  var c2 = (item2.color || item2.extractedColor || '').toLowerCase();
  if (!c1 || !c2) return 60;
  if (c1 === c2) return 80;
  var n1 = NEUTRALS.some(function(n){ return c1.includes(n); });
  var n2 = NEUTRALS.some(function(n){ return c2.includes(n); });
  if (n1 && n2) return 90;
  if (n1 || n2) return 75;
  return 50;
}

function bestMatch(candidates, reference, tempC) {
  if (!candidates || candidates.length === 0) return null;
  var scored = candidates.map(function(c) {
    return {
      item: c,
      score: colorCompatibilityScore(c.color, reference.color) * 0.6
           + fabricSeasonScore(c, tempC) * 0.4,
    };
  });
  scored.sort(function(a, b) { return b.score - a.score; });
  return scored[0].score > 0.4 ? scored[0].item : null;
}

function makeOutfit(items, tempC, occasion, type) {
  var score = scoreOutfit(items, tempC, occasion);
  var season = getSeason(tempC);
  var colors = [...new Set(items.map(function(i) { return i.color; }).filter(Boolean))];

  // Générer une description cohérente
  var desc = buildOutfitDescription(items, tempC, occasion);

  var occLabel = occasion || 'Casual';
  return {
    id:      'outfit_' + occLabel + '_' + items.map(function(i) { return i.id; }).join('_'),
    type:    type,
    items:   items,
    score:   score,
    season:  season,
    tempC:   tempC,
    colors:  colors,
    occasion: occLabel,
    description: desc,
    missingCategories: getMissingCategories(items, tempC),
  };
}

function buildOutfitDescription(items, tempC, occasion) {
  var cats    = items.map(function(i) { return i.category; });
  var colors  = items.map(function(i) { return i.color; }).filter(Boolean);
  var mainColor = colors[0] || '';
  var season  = getSeason(tempC);

  var OCC_DESC = {
    'Casual':  'Look décontracté',
    'Soirée':  'Tenue de soirée',
    'Bureau':  'Look professionnel',
    'Chic':    'Look chic',
    'Sport':   'Tenue sportive',
    'Dîner':   'Tenue pour dîner',
  };

  var base = OCC_DESC[occasion] || (
    season === 'été' ? 'Look estival' :
    season === 'hiver' ? 'Tenue chaude' :
    'Tenue mi-saison'
  );

  var parts = [base];
  if (cats.includes('Robes')) parts.push('en robe');
  else if (cats.includes('Hauts') && cats.includes('Bas')) parts.push('haut + bas');
  if (mainColor) parts.push('tons ' + mainColor.toLowerCase());

  return parts.join(' · ');
}

function getMissingCategories(items, tempC) {
  var cats = items.map(function(i) { return i.category; });
  var missing = [];

  // Pièces essentielles
  var hasBase = cats.includes('Robes') || (cats.includes('Hauts') && cats.includes('Bas'));
  if (!hasBase) {
    if (!cats.includes('Hauts')) missing.push('Hauts');
    if (!cats.includes('Bas'))   missing.push('Bas');
  }
  if (!cats.includes('Chaussures')) missing.push('Chaussures');
  if (tempC < 15 && !cats.includes('Vestes')) missing.push('Vestes');

  // Pièces complémentaires (suggérées seulement si manquent aussi les essentielles)
  if (missing.length < 3) {
    if (!cats.includes('Sacs')) missing.push('Sacs');
  }
  if (missing.length < 3) {
    if (!cats.includes('Bijoux') && !cats.includes('Accessoires')) missing.push('Bijoux');
  }

  return missing;
}

// Import dynamique des vrais articles Zalando
var _zalandoFallbacks = null;
function getZalandoFallbacks(cat, gender) {
  if (!_zalandoFallbacks) {
    try { _zalandoFallbacks = require('./ZalandoService'); } catch(e) { return []; }
  }
  if (_zalandoFallbacks && _zalandoFallbacks.getFallbackProducts) {
    return _zalandoFallbacks.getFallbackProducts(gender || 'F', cat) || [];
  }
  return [];
}

/* ══════════════════════════════════════════════════════════
   6. SUGGESTIONS CATALOGUE POUR COMPLÉTER
══════════════════════════════════════════════════════════ */

// Fallback avec vraies images et vrais liens partenaires
var FALLBACK_SUGGESTIONS = {
  'Bijoux': [
    { id:'fb_j1', category:'Bijoux', name:'Collier chaine dore', brand:'ASOS Design', price:14.99, color:'Or', emoji:'📿', partner:'asos',
      image:'https://images.asos-media.com/products/asos-design-collier-chaine-dore/201623374-1-gold',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.asos.com/fr/asos-design/asos-design-collier-chaine-dore/prd/201623374'),
      originalUrl:'https://www.asos.com/fr/asos-design/asos-design-collier-chaine-dore/prd/201623374' },
    { id:'fb_j2', category:'Bijoux', name:'Collier dore fin', brand:'ONLY', price:12.99, color:'Or', emoji:'💍', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/2c25928df36849f8a557e2027353d372/86d73713151444ebb6a79a0cd185a4d6.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/only-collier-or-on551l1dc-f11.html'),
      originalUrl:'https://www.zalando.fr/only-collier-or-on551l1dc-f11.html' },
    { id:'fb_j3', category:'Bijoux', name:'Boucles oreilles argent', brand:'H&M', price:9.99, color:'Argent', emoji:'💎', partner:'hm',
      image:'https://lp2.hm.com/hmgoepprod?set=quality%5B79%5D%2Csource%5B%2F2f%2F4c%2F2f4c8b3a54bed3ef4b7c7b9a3c9d9b0e2c3f4d5e.jpg%5D',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www2.hm.com/fr_fr/femme/accessoires/bijoux.html'),
      originalUrl:'https://www2.hm.com/fr_fr/femme/accessoires/bijoux.html' },
    { id:'fb_j4', category:'Bijoux', name:'Bracelet dore', brand:'Pieces', price:16.99, color:'Or', emoji:'📿', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/5c35128df36849f8a557e2027353d372/96d73713151444ebb6a79a0cd185a4d6.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/pieces-bracelet-dore-pi951l09d-f11.html'),
      originalUrl:'https://www.zalando.fr/pieces-bracelet-dore-pi951l09d-f11.html' },
    { id:'fb_j5', category:'Bijoux', name:'Bague minimaliste', brand:'ASOS Design', price:8.99, color:'Argent', emoji:'💍', partner:'asos',
      image:'https://images.asos-media.com/products/asos-design-bague/104563215-1-silver',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.asos.com/fr/asos-design/asos-design-pack-de-3-bagues-minimalistes/prd/104563215'),
      originalUrl:'https://www.asos.com/fr/asos-design/asos-design-pack-de-3-bagues-minimalistes/prd/104563215' },
  ],
  'Accessoires': [
    { id:'fb_a1', category:'Accessoires', name:'Ceinture noire en cuir', brand:'Vero Moda', price:19.99, color:'Noir', emoji:'🎀', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/aa35028df36849f8a557e2027353d372/76d73713151444ebb6a79a0cd185a4d6.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/vero-moda-accessoires-noir-vm151e0d6-q11.html'),
      originalUrl:'https://www.zalando.fr/vero-moda-accessoires-noir-vm151e0d6-q11.html' },
    { id:'fb_a2', category:'Accessoires', name:'Echarpe douce beige', brand:'H&M', price:14.99, color:'Beige', emoji:'🧣', partner:'hm',
      image:'https://lp2.hm.com/hmgoepprod?set=quality%5B79%5D%2Csource%5B%2Fac%2Fce%2Facce3b3a54bed3ef4b7c7b9a3c9d9b0e2c3f4d5e.jpg%5D',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www2.hm.com/fr_fr/femme/accessoires/echarpes-et-foulards.html'),
      originalUrl:'https://www2.hm.com/fr_fr/femme/accessoires/echarpes-et-foulards.html' },
    { id:'fb_a3', category:'Accessoires', name:'Lunettes de soleil', brand:'ASOS Design', price:18.99, color:'Noir', emoji:'🕶️', partner:'asos',
      image:'https://images.asos-media.com/products/asos-design-lunettes-cat-eye/200412856-1-black',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.asos.com/fr/asos-design/asos-design-lunettes-de-soleil-cat-eye/prd/200412856'),
      originalUrl:'https://www.asos.com/fr/asos-design/asos-design-lunettes-de-soleil-cat-eye/prd/200412856' },
  ],
  'Chaussures': [
    { id:'fb_s1', category:'Chaussures', name:'Escarpins noirs', brand:'Anna Field', price:44.99, color:'Noir', emoji:'👠', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/f1a4d01abb0a41a9bc30a8e8cdebae91/4ea1e3a68e84498aa1e3d32e00ae6219.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/anna-field-escarpins-black-an611a1ct-q11.html'),
      originalUrl:'https://www.zalando.fr/anna-field-escarpins-black-an611a1ct-q11.html' },
    { id:'fb_s2', category:'Chaussures', name:'Sneakers blanches', brand:'ASOS Design', price:34.99, color:'Blanc', emoji:'👟', partner:'asos',
      image:'https://images.asos-media.com/products/asos-design-sneakers/206543214-1-white',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.asos.com/fr/asos-design/asos-design-chunky-sneakers/prd/206543214'),
      originalUrl:'https://www.asos.com/fr/asos-design/asos-design-chunky-sneakers/prd/206543214' },
    { id:'fb_s3', category:'Chaussures', name:'Mules a talon carre', brand:'H&M', price:29.99, color:'Beige', emoji:'👡', partner:'hm',
      image:'https://lp2.hm.com/hmgoepprod?set=quality%5B79%5D%2Csource%5B%2Fbc%2Fde%2Fbcde3b3a54bed3ef4b7c7b9a3c9d9b0e2c3f4d5e.jpg%5D',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www2.hm.com/fr_fr/femme/chaussures/sandales-et-mules.html'),
      originalUrl:'https://www2.hm.com/fr_fr/femme/chaussures/sandales-et-mules.html' },
  ],
  'Sacs': [
    { id:'fb_b1', category:'Sacs', name:'Sac a main camel', brand:'Vero Moda', price:34.99, color:'Camel', emoji:'👜', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/3c25928df36849f8a557e2027353d372/46d73713151444ebb6a79a0cd185a4d6.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/vero-moda-sac-a-main-camel-vm151h0a4-b11.html'),
      originalUrl:'https://www.zalando.fr/vero-moda-sac-a-main-camel-vm151h0a4-b11.html' },
    { id:'fb_b2', category:'Sacs', name:'Mini sac bandouliere noir', brand:'ASOS Design', price:24.99, color:'Noir', emoji:'👛', partner:'asos',
      image:'https://images.asos-media.com/products/asos-design-mini-sac/205123456-1-black',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.asos.com/fr/asos-design/asos-design-mini-sac-bandouliere/prd/205123456'),
      originalUrl:'https://www.asos.com/fr/asos-design/asos-design-mini-sac-bandouliere/prd/205123456' },
    { id:'fb_b3', category:'Sacs', name:'Tote bag noir', brand:'H&M', price:19.99, color:'Noir', emoji:'🛍️', partner:'hm',
      image:'https://lp2.hm.com/hmgoepprod?set=quality%5B79%5D%2Csource%5B%2Fdc%2Fef%2Fdcef3b3a54bed3ef4b7c7b9a3c9d9b0e2c3f4d5e.jpg%5D',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www2.hm.com/fr_fr/femme/accessoires/sacs.html'),
      originalUrl:'https://www2.hm.com/fr_fr/femme/accessoires/sacs.html' },
  ],
  'Vestes': [
    { id:'fb_v1', category:'Vestes', name:'Blazer noir structure', brand:'Selected Femme', price:89.99, color:'Noir', emoji:'🧥', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/1c25928df36849f8a557e2027353d372/16d73713151444ebb6a79a0cd185a4d6.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/selected-femme-blazer-black-sfd21h006-q11.html'),
      originalUrl:'https://www.zalando.fr/selected-femme-blazer-black-sfd21h006-q11.html' },
    { id:'fb_v2', category:'Vestes', name:'Trench coat beige', brand:'ASOS Design', price:74.99, color:'Beige', emoji:'🧥', partner:'asos',
      image:'https://images.asos-media.com/products/asos-design-trench/203456789-1-beige',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.asos.com/fr/asos-design/asos-design-trench-coat/prd/203456789'),
      originalUrl:'https://www.asos.com/fr/asos-design/asos-design-trench-coat/prd/203456789' },
  ],
  'Hauts': [
    { id:'fb_h1', category:'Hauts', name:'Top en soie blanc', brand:'Vero Moda', price:29.99, color:'Blanc', emoji:'👚', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/6c25928df36849f8a557e2027353d372/76d73713151444ebb6a79a0cd185a4d6.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/vero-moda-top-blanc-vm151d0e2-a11.html'),
      originalUrl:'https://www.zalando.fr/vero-moda-top-blanc-vm151d0e2-a11.html' },
    { id:'fb_h2', category:'Hauts', name:'Chemise en lin blanche', brand:'H&M', price:24.99, color:'Blanc', emoji:'👔', partner:'hm',
      image:'https://lp2.hm.com/hmgoepprod?set=quality%5B79%5D%2Csource%5B%2Fec%2Fef%2Fecef3b3a54bed3ef4b7c7b9a3c9d9b0e2c3f4d5e.jpg%5D',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www2.hm.com/fr_fr/femme/hauts/chemises.html'),
      originalUrl:'https://www2.hm.com/fr_fr/femme/hauts/chemises.html' },
  ],
  'Bas': [
    { id:'fb_p1', category:'Bas', name:'Jean slim noir taille haute', brand:'ONLY', price:39.99, color:'Noir', emoji:'👖', partner:'zalando',
      image:'https://img01.ztat.net/article/spp-media-p1/7c25928df36849f8a557e2027353d372/86d73713151444ebb6a79a0cd185a4d6.jpg?imwidth=500',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www.zalando.fr/only-jean-slim-black-on321n0eo-q11.html'),
      originalUrl:'https://www.zalando.fr/only-jean-slim-black-on321n0eo-q11.html' },
    { id:'fb_p2', category:'Bas', name:'Pantalon tailleur beige', brand:'H&M', price:34.99, color:'Beige', emoji:'👖', partner:'hm',
      image:'https://lp2.hm.com/hmgoepprod?set=quality%5B79%5D%2Csource%5B%2Ffc%2Fef%2Ffcef3b3a54bed3ef4b7c7b9a3c9d9b0e2c3f4d5e.jpg%5D',
      affiliateUrl:'https://go.redirectingat.com/?id=301454X1789418&url=' + encodeURIComponent('https://www2.hm.com/fr_fr/femme/pantalons.html'),
      originalUrl:'https://www2.hm.com/fr_fr/femme/pantalons.html' },
  ],
};

export function suggestCatalogItems(outfit, catalogItems, maxTotal) {
  maxTotal = maxTotal || 10;
  if (!outfit) return [];

  var outfitItems  = outfit.items || [];
  var outfitColors = outfitItems.map(function(i) { return i.color; }).filter(Boolean);
  var ownedCats    = new Set(outfitItems.map(function(i) { return i.category; }));
  var tempC        = outfit.tempC || 18;

  // Catégories manquantes essentielles
  var essentialMissing = [];
  var hasBase = ownedCats.has('Robes') || (ownedCats.has('Hauts') && ownedCats.has('Bas'));
  if (!hasBase) {
    if (!ownedCats.has('Hauts')) essentialMissing.push('Hauts');
    if (!ownedCats.has('Bas'))   essentialMissing.push('Bas');
  }
  if (!ownedCats.has('Chaussures')) essentialMissing.push('Chaussures');
  if (tempC < 15 && !ownedCats.has('Vestes')) essentialMissing.push('Vestes');

  // Catégories d'accessoires — TOUJOURS suggérées si absentes
  var accessoryMissing = [];
  if (!ownedCats.has('Sacs'))        accessoryMissing.push('Sacs');
  if (!ownedCats.has('Bijoux'))      accessoryMissing.push('Bijoux');
  if (!ownedCats.has('Accessoires')) accessoryMissing.push('Accessoires');

  // Ordre : essentiels d'abord, puis accessoires
  var allCats = essentialMissing.concat(accessoryMissing);
  if (allCats.length === 0) {
    // Outfit complet → suggérer quand même Bijoux et Accessoires pour enrichir
    allCats = ['Bijoux', 'Accessoires', 'Sacs'];
  }

  var catalog = Array.isArray(catalogItems) ? catalogItems : [];

  // ═══ OPTIMISATION : utiliser l'index catalogue (évite de filtrer 5000 items N fois) ═══
  var catIdx = null;
  try {
    var CatalogIndex = require('./CatalogIndexService');
    catIdx = CatalogIndex.buildCatalogIndex(catalog);
  } catch(e) { /* fail open, fallback sur filter direct */ }

  var suggestions = [];
  var usedIds     = new Set();
  var perCat      = Math.max(2, Math.ceil(maxTotal / allCats.length));

  allCats.forEach(function(cat) {
    // 1. Récupérer candidats via index (rapide) ou fallback filter
    var candidates;
    if (catIdx && catIdx.byCategory && catIdx.byCategory[cat]) {
      candidates = catIdx.byCategory[cat].filter(function(p) {
        return p.price > 0 && !usedIds.has(p.id);
      }).slice(0, 150); // cap pool à 150 pour perf
    } else {
      candidates = catalog.filter(function(p) {
        return p.category === cat && p.price > 0 && !usedIds.has(p.id);
      });
    }

    // ═══ FILTRE SPÉCIFIQUE SACS ═══ : éviter sac à dos / sport pour looks formels
    if (cat === 'Sacs') {
      try {
        var AIStylist = require('./AIStylistService');
        if (AIStylist && AIStylist.isBagAppropriate) {
          var outfitFormality = outfitItems.length > 0
            ? outfitItems.reduce(function(s, i) { return s + (i.ai_metadata && i.ai_metadata.formality || 3); }, 0) / outfitItems.length
            : 3;
          var outfitOccasion = outfit.occasion || '';
          var outfitGender = outfit.gender || 'F';
          candidates = candidates.filter(function(p) {
            return AIStylist.isBagAppropriate(p, outfitOccasion, outfitFormality, outfitGender);
          });
          // Bonus tri gender-aware : priorise handbag/clutch/tote pour femme
          if (AIStylist.scoreBagGender) {
            candidates.sort(function(a, b) {
              return AIStylist.scoreBagGender(b, outfitGender) - AIStylist.scoreBagGender(a, outfitGender);
            });
          }
        }
      } catch(e) { /* fail open : garder tous les candidats */ }
    }

    var scored = candidates.map(function(p) {
      var colorScore = outfitColors.length > 0
        ? outfitColors.reduce(function(b, c) { return Math.max(b, colorCompatibilityScore(p.color, c)); }, 0.4)
        : 0.6;

      // Priorité au scoring météo IA (temperatureRange + seasonality), fallback fabricSeasonScore
      var weatherScore = 0.5;
      try {
        var CatIdx = require('./CatalogIndexService');
        if (CatIdx && CatIdx.scoreWeatherMatch) {
          weatherScore = CatIdx.scoreWeatherMatch(p, tempC) / 100;
        } else {
          weatherScore = fabricSeasonScore(p, tempC);
        }
      } catch (e) {
        weatherScore = fabricSeasonScore(p, tempC);
      }

      return { item: p, score: colorScore * 0.6 + weatherScore * 0.4 };
    });
    scored.sort(function(a, b) { return b.score - a.score; });
    var best = scored.slice(0, perCat).map(function(s) {
      usedIds.add(s.item.id);
      return Object.assign({}, s.item, {
        matchScore:  Math.max(50, Math.round(s.score * 100)),
        matchReason: buildMatchReason(s.item, outfit, s.score),
        missingFor:  cat,
      });
    });

    // Pas de fallback statique — uniquement vrais articles du catalogue réel

    suggestions = suggestions.concat(best);
  });

  return suggestions.slice(0, maxTotal);
}

function colorMatch(color, outfitColors) {
  if (!color || outfitColors.length === 0) return 'Complète votre tenue';
  var neutral = ['Noir','Blanc','Beige','Camel','Gris','Marine'];
  if (neutral.indexOf(color) !== -1) return "Couleur neutre, s'assortit facilement";
  var match = outfitColors.some(function(c) { return colorCompatibilityScore(color, c) > 0.75; });
  return match ? 'Couleur assortie à votre tenue' : 'Complète votre tenue';
}
function buildMatchReason(catalogItem, outfit, colorScore) {
  var reasons = [];
  if (colorScore >= 0.9)      reasons.push('✓ Couleur parfaite');
  else if (colorScore >= 0.75) reasons.push('✓ Couleur compatible');

  var season = getSeason(outfit.tempC || 18);
  var seasonScore = fabricSeasonScore(catalogItem, outfit.tempC || 18);
  if (seasonScore >= 0.9) reasons.push('✓ Adapté à la météo');

  if (reasons.length === 0) reasons.push('✓ Complète la tenue');
  return reasons.join(' · ');
}

/* ══════════════════════════════════════════════════════════
   7. EXPORT UTILITAIRES
══════════════════════════════════════════════════════════ */
export function getTemperatureFromWeather(weatherData) {
  if (!weatherData) return 18;
  return parseFloat(weatherData.temperature || weatherData.temp || weatherData.current?.temp_c || 18);
}

export function colorCompatibility(c1, c2) {
  return colorCompatibilityScore(c1, c2);
}

export { getSeason, getRecommendedCategories };
